import { map, call } from 'ramda';

const DEFAULT_ACTIVITY_EVENTS = [
    'click',
    'keydown',
    'DOMMouseScroll',
    'mousewheel',
    'touchstart',
    'touchmove'
];

// state: 'active', 'idle', 'init' (---INIT---IDLE---ACTIVE---)
export const ACTIVE = 'active';
export const IDLE = 'idle';
export const INIT = 'init';

// key to store the current state of the activity detector, so we can share it between tabs
export const ACTIVITY_STATE_KEY = 'ACTIVITY_STATE_KEY';

// activity detector hooks into window listeners and checks whether user triggered any of
// the desired actions. there's two main methods provided to trigger when state changes.
const activityDetector = ({
    activityEvents = DEFAULT_ACTIVITY_EVENTS,
    timeToIdle = 30000,
    autoInit = true
} = {}) => {
    let listeners = { [ACTIVE]: [], [IDLE]: [] };
    let timer;

    const changeState = (newState) => {
        window?.localStorage?.setItem?.(ACTIVITY_STATE_KEY, newState);
    };

    const handleActiveEvent = () => {
        map(call, listeners[ACTIVE]);
    };

    const handleIdleEvent = () => {
        clearTimeout(timer);
        changeState(IDLE);
        map(call, listeners[IDLE]);
    };

    const handleInitEvent = () => {
        clearTimeout(timer);
        timer = setTimeout(handleIdleEvent, timeToIdle);
    };

    // initializes the activity detector
    const handleInitActivityDetect = () => {
        changeState(INIT);
        handleInitEvent();
    };

    // checks the current state of the activity detector
    const handleCheckActivityState = () => {
        const state = window?.localStorage?.getItem?.(ACTIVITY_STATE_KEY);
        if (state === IDLE) {
            handleActiveEvent();
        }
        changeState(ACTIVE);
        handleInitActivityDetect();
    };

    // listens to storage events from other tabs and triggers the appropriate event
    const handleStorageListener = (storageEvent = {}) => {
        const { isTrusted, key, newValue, oldValue } = storageEvent;

        if (isTrusted && key === ACTIVITY_STATE_KEY) {
            if (newValue === INIT) {
                handleInitEvent();
            }
            if (newValue === ACTIVE && oldValue === IDLE) {
                handleActiveEvent();
            }
        }
    };

    // initialize the listeners and sets state to initialState
    const init = () => {
        handleCheckActivityState();
        map((name) => window.addEventListener(name, handleCheckActivityState), activityEvents);
        window.addEventListener('storage', handleStorageListener);
    };

    // hooks function `listener` to be triggered when `stateName` happens
    // (listener can be one of 'idle', 'active' state names)
    const on = (stateName, listener) => {
        listeners[stateName].push(listener);
        const off = () => {
            const index = listeners[stateName].indexOf(listener);
            if (index >= 0) {
                listeners[stateName].splice(index, 1);
            }
        };
        return off;
    };

    // destruct all the listeners, because we're neat
    const stop = () => {
        listeners = { [ACTIVE]: [], [IDLE]: [] };

        clearTimeout(timer);
        map((name) => window.removeEventListener(name, handleCheckActivityState), activityEvents);
        window.removeEventListener('storage', handleStorageListener);
    };

    if (autoInit) {
        init();
    }

    return { on, stop, init };
};

export default activityDetector;
